<template>
  <div class="connections-container" ref="svgElement">
    <svg :width="svgWidth + 'px'" :height="svgHeight + 'px'" class="svg-container">
      <path
        v-for="(obj, connectionIndex) in preparedConnections"
        :key="'connection' + connectionIndex"
        :d="obj.connection.line"
        :stroke-dasharray="obj.connection.mode === 'PASSING' ? '5 3' : ''"
        class="connection_line"
        :class="obj.connection.mode.toLowerCase()"
      />
    </svg>
    <div 
      v-for="(obj, connectionIndex) in cleanDuplicatedSplitter(preparedConnections)"
      v-if="obj.hasSplitter" 
      :key="connectionIndex"
      class="img-container"
      :style="{top: `${obj.splitterPosition.height}px`, left: `${obj.splitterPosition.width}px`}"
      @click="selectSplitter(obj.splitter)"
      @contextmenu="$event => openConnectionPointMenu($event, obj)"
    >
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <p 
            class="text-ellipsis" 
            v-bind="attrs"
            v-on="on"
          >
            {{ obj.splitter.sp_label }}
          </p>
        </template>
        <span :style="{fontSize: '10px'}">{{ obj.splitter.sp_label  }}</span>
      </v-tooltip>
      <img :src="require(`@/assets/splitter-1.png`)" :class="{'splitter-opacity': isSplitterSelected(obj.splitter.sp_id)}"/> 
    </div>
  </div>
</template>

<script>
import { splicingObjectItemsListMixin } from "@/mixins/splicingObjectItemsListMixin";
import _ from "lodash";

export default {
  name: "ClosureFormSplicingTabConnections",
  mixins: [splicingObjectItemsListMixin],
  props: {
    objectUuid: String,

    connections: Array,

    originObjects: Array,

    destinationObjects: Array,

    updateCounter: Number,

    splitters: Array,

    equipmentSet: Object,

    isTechLocal: {
      type: Boolean,
      default: false
    },
    selectedDestination: {
      type: Object
    },
    selectedOrigin: {
      type: Object
    }
  },
  data() {
    return {
      preparedConnections: [],
      splittersCache: [],
      menu: {
        isOpen: false,
        x: 0,
        y: 0
      },

      svgHeight: 0,
      svgWidth: 0
    };
  },
  watch: {
    updateCounter() {
      this.svgHeight = 0;
      this.$nextTick(() => this.prepareConnections());
    },
    svgWidth(){
      this.$nextTick(() => this.prepareConnections());
    },
    preparedConnections(value){
      const splittersId = [];
      const newSplittersCache = [];
      //update splittersCache when splitter is deleted
      value.forEach(connection => {
        if(connection.hasSplitter){
          const splitterIsFound = splittersId.find(item => item === connection.splitter.sp_id);
          if(!splitterIsFound){
            splittersId.push(connection.splitter.sp_id);
          }
        }
      });
      splittersId.forEach(id => {
        const splitterCached = this.splittersCache.find(item => item.id === id);
        if(splitterCached){
          newSplittersCache.push(splitterCached);
        }
      });
      this.splittersCache = [...newSplittersCache];
    },
    equipmentSet(labelSetData) {
      this.preparedConnections.forEach(connection => {
        if(connection.hasSplitter) {
          if(labelSetData.orig_label === connection.splitter.sp_label){
            connection.splitter.sp_label = labelSetData.update_label;
          }
        }
      });
    },
  },

  mounted() {
    this.svgHeight = 0;
    this.$nextTick(() => this.prepareConnections());
  },
  methods: {
    prepareConnections() {
      let preparedConnections = [];
      this.svgHeight = document.querySelector(
        "#splicing-connections-container-" + this.objectUuid
      ).scrollHeight;
      this.svgWidth = this.$refs.svgElement.clientWidth;
      let anchorUnit = this.svgWidth / 8;

      if (this.connections && this.connections.length > 0) {
        for (const connection of this.connections) {
          if(this.hasOriginAndDestination(connection)){
            let sideOrigin = this.getSideOrigin(connection);
            let objectsOrigin = [...this.originObjects];

            let sideDestination = this.getSideDestination(connection);
            let objectsDestination = [...this.destinationObjects];

            if (sideOrigin === "destination") {
              objectsOrigin = this.destinationObjects;
            }
            if (sideDestination === "origin") {
              objectsDestination = this.originObjects;
            }

            let originIndexes = this.getObjectAndEntryIndexes(
              connection.origin,
              objectsOrigin,
              sideOrigin
            );
            let destinationIndexes = this.getObjectAndEntryIndexes(
              connection.destination,
              objectsDestination,
              sideDestination
            );
            if (originIndexes && destinationIndexes) {
              let firstAnchorValue = 4;
              let secondAnchorValue = 5;
              let left = this.getItemTopPosition(originIndexes, objectsOrigin, "origin", destinationIndexes, objectsDestination, "destination");
              let right = this.getItemTopPosition(destinationIndexes, objectsDestination, "destination", originIndexes, objectsOrigin, "origin");
              const objTypes =  this.getObjectTypes(
                {indexes: originIndexes,cablesAndSplitters: objectsOrigin}, 
                {indexes: destinationIndexes,cablesAndSplitters: objectsDestination}
              );
              const position = this.getObjectWidthAndStart(
                {indexes: originIndexes,cablesAndSplitters: objectsOrigin}, 
                {indexes: destinationIndexes,cablesAndSplitters: objectsDestination}
              );
              let side;
              let sameSide = sideOrigin === sideDestination;
              if (sameSide) {
                side = sideOrigin;
              }
              if(objTypes.originIsCable && !objTypes.destinationIsCable){
                firstAnchorValue = 0.6;
                secondAnchorValue = 0.8;
              }else if(!objTypes.originIsCable && objTypes.destinationIsCable){
                firstAnchorValue = 5;
                secondAnchorValue = 6;
              }
              let preparedConnection = _.cloneDeep(connection);
              preparedConnection.line = this.getConnectionLineCoordinates(
                position.start,
                left,
                right,
                position.width,
                anchorUnit,
                sameSide,
                side,
                firstAnchorValue,
                secondAnchorValue,
                position.waterfall,
                position.waterfallEnd,
                position.sameSideSplitter,
                position.sameSideNivel
              );
              if(position.isStart){
                let positionHeight = right - 28;
                let positionWidth = position.waterfall > 1 ? position.start + 45 : position.start + (this.svgWidth/4.2);
                if(this.isTechLocal && position.waterfall === 0 && position.oltStart){
                  positionHeight = left - 28;
                  positionWidth = position.start - 15;
                }
                preparedConnections.push(
                  {
                    connection: preparedConnection, 
                    hasSplitter: true, 
                    splitterPosition: {
                      width: positionWidth,
                      height: positionHeight
                    },
                    splitter: position.obj
                  }
                );
              }else{
                preparedConnections.push({connection: preparedConnection, hasSplitter: false});
              }
            }
          }
        }
      }
      this.preparedConnections = preparedConnections;
    },
    hasOriginAndDestination(connection){
      return !!connection.destination.id && !!connection.origin.id
    },
    cleanDuplicatedSplitter(connections){
      //avoid displaying more than one img per connection
      const splittersIds = [];
      const newConnections = connections.map(connection => {
        if(connection.hasSplitter && !splittersIds.find(item => item === connection.splitter.sp_id)){
          splittersIds.push(connection.splitter.sp_id);
          return connection
        }
        connection.hasSplitter = false;
        return connection;
      });
      return newConnections;
    },
    getCablesAndSplitters(lists){
      const cables = [];
      const splitters = [];
      lists.forEach(item => {
        if(this.isCable(item)){
          cables.push({...item});
        }else{
          splitters.push({...item});
        }
      });
      return {cables, splitters}
    },
    getSideOrigin(connection) {
      let side = "origin";
      if (
        !this.originObjects.find(item => {
          let uuid;
          if (this.isCable(item)) {
            uuid = "cb_uuid";
          } else if (this.isSplitter(item)) {
            uuid = "sp_uuid";
          }
          return item[uuid] === connection.origin.parent.uuid;
        })
      ) {
        side = "destination";
      }
      return side;
    },

    getSideDestination(connection) {
      let side = "destination";
      if (
        !this.destinationObjects.find(item => {
          let uuid;
          if (this.isCable(item)) {
            uuid = "cb_uuid";
          } else if (this.isSplitter(item)) {
            uuid = "sp_uuid";
          }
          return item[uuid] === connection.destination.parent.uuid;
        })
      ) {
        side = "origin";
      }
      return side;
    },

    getObjectAndEntryIndexes(fiberOrPort, cablesAndSplitters, side) {
      if (fiberOrPort.type === "fiber") {
        return this.getCableAndFiberIndexes(fiberOrPort, cablesAndSplitters);
      } else if (fiberOrPort.type === "port") {
        return this.getSplitterAndPortIndexes(
          fiberOrPort,
          cablesAndSplitters,
          side
        );
      }
    },

    getCableAndFiberIndexes(fiber, cablesAndSplitters) {
      let cableIndex = cablesAndSplitters.findIndex(
        object => this.isCable(object) && object.cb_id === fiber.parent.id
      );
      if (cableIndex > -1) {
        let fiberIndex = cablesAndSplitters[cableIndex].fibers.findIndex(
          item => item.fb_id === fiber.id
        );

        if (fiberIndex > -1) {
          return {
            objectIndex: cableIndex,
            entryIndex: fiberIndex
          };
        }
      }
    },

    getSplitterAndPortIndexes(port, cablesAndSplitters, side) {
      let splitterIndex = cablesAndSplitters.findIndex(
        object => this.isSplitter(object) && object.sp_id === port.parent.id
      );
      if (splitterIndex > -1) {
        let portType = "inPorts";
        if (side === "origin") {
          portType = "outPorts";
        }
        let portIndex = cablesAndSplitters[splitterIndex][portType].findIndex(
          item => item.pt_id === port.id
        );
        if (portIndex > -1) {
          return {
            objectIndex: splitterIndex,
            entryIndex: portIndex,
            portType: portType
          };
        }
      }
    },
    getObjectTypes(origin, destination){
      const originObjIndex = origin.indexes.objectIndex;
      const originCableOrSplitter = origin.cablesAndSplitters[originObjIndex];
      const destinationObjIndex = destination.indexes.objectIndex;
      const destinationCableOrSplitter = destination.cablesAndSplitters[destinationObjIndex];
      return {
        originIsCable: this.isCable(originCableOrSplitter),
        originObj: originCableOrSplitter,
        destinationIsCable: this.isCable(destinationCableOrSplitter),
        destinationObj: destinationCableOrSplitter
      }

    },
    getItemTopPosition(indexes, cablesAndSplitters, direction, oppositeIndexes, oppositeCablesAndSplitters, oppositeDirection) {
      const allMargins = this.getAllObjectMargins(indexes,cablesAndSplitters,direction, oppositeIndexes, oppositeCablesAndSplitters, oppositeDirection);
      let rowHeight = 25;
      return (
        (rowHeight / 2) + allMargins
      );
    },
    getObjectWidthAndStart(origin, destination){
      let position;
      //splittersWidthCache
      const originObjIndex = origin.indexes.objectIndex;
      const originCableOrSplitter = origin.cablesAndSplitters[originObjIndex];
      const destinationObjIndex = destination.indexes.objectIndex;
      const destinationCableOrSplitter = destination.cablesAndSplitters[destinationObjIndex];
      //cable to splitter
      if(this.isCable(originCableOrSplitter) && this.isSplitter(destinationCableOrSplitter)){
        position = {width: this.svgWidth/3.9, start: 0, isStart: true, obj: destinationCableOrSplitter, waterfall: 0, waterfallEnd: false, sameSideSplitter: false, sameSideNivel: 0, oltStart: false};
      }else if(this.isCable(destinationCableOrSplitter) && this.isSplitter(originCableOrSplitter)){
        //splitter to cable
        const isOrigin = this.originObjects.find(item => item.cb_id === destinationCableOrSplitter.cb_id);
        const nivel = this.getSplitterHierarchy(originCableOrSplitter);
        if(!isOrigin){
          const startPosition = nivel > 2 ? (this.svgWidth/4) + 45 + (30 * nivel) : (this.svgWidth/4) + 10
          if(this.isTechLocal && nivel <= 2){
            //show splitter icon when is tech local so user can create waterfall without origin cable
            position = {width: this.svgWidth, start: startPosition, isStart: true, obj: originCableOrSplitter, waterfall: 0, waterfallEnd: true, sameSideSplitter: false, sameSideNivel: nivel, oltStart: true};
          }else{
            position = {width: this.svgWidth, start: startPosition, isStart: false, waterfall: 0, waterfallEnd: true, sameSideSplitter: false, sameSideNivel: nivel, oltStart: false};
          }
        }else{
          const startPosition = nivel > 2 ? (this.svgWidth/4) - 15 + (30 * nivel) : (this.svgWidth/4) + 10
          position = {width: this.svgWidth, start: startPosition, isStart: false, waterfall: 0, waterfallEnd: false, sameSideSplitter: true, sameSideNivel: nivel, oltStart: false};

        }
      }else if(this.isSplitter(originCableOrSplitter) && this.isSplitter(destinationCableOrSplitter)){
        //splitter
        const nivel = this.getSplitterHierarchy(originCableOrSplitter);
        const startPosition = nivel > 2 ? (this.svgWidth/4) + 10 + (20 * nivel) : (this.svgWidth/4) + 10
        position = {width: (this.svgWidth/4) + (10 * nivel), start: startPosition, isStart: true, obj: destinationCableOrSplitter, waterfall: nivel, waterfallEnd: false, sameSideSplitter: false, sameSideNivel: nivel, oltStart: false};
      }else{
        position = {width: this.svgWidth, start: 0, isStart: false, waterfall: 0, waterfallEnd: false, sameSideSplitter: false, sameSideNivel: 0, oltStart: false};
      }
      return position;

    },
    getSplitterHierarchy(splitter){
      const splitters = [];
      this.getSpilttersConnected(splitter.sp_id, splitters);
      //delete duplicates from array
      const jsonObject = splitters.map(JSON.stringify);
      const uniqueSet = new Set(jsonObject);
      const uniqueArray = Array.from(uniqueSet).map(JSON.parse);
      return (uniqueArray.length + 1);
    },
    getSpilttersConnected(spId, spArray){
      //check if splitter was already added
      let hasOutportConnected = false;
      const findSplitter = spArray.find(item => item.sp_id === spId);
      if(!!findSplitter || !(!!spId)){
        return;
      }
      const splitter = this.splitters.find(splitter => splitter.sp_id === spId);
      let nextSpId;
      splitter.inPorts.forEach(port => {
        if(!!port.connectedPort){
          hasOutportConnected = true;
          const checkSplitterExists = spArray.find(item => item.sp_id === port.connectedPort.pt_splitter_id);
          if(!(!!checkSplitterExists)){
            nextSpId = port.connectedPort.pt_splitter_id;
            spArray.push({
              sp_id: splitter.sp_id,
            });
          }
        }
      });
      if(!hasOutportConnected){
        spArray.push({
          sp_id: splitter.sp_id,
        });
      }
      this.getSpilttersConnected(nextSpId, spArray);
    },
    getAllObjectMargins(indexes,cablesAndSplitters, direction, oppositeIndexes, oppositeCablesAndSplitters, oppositeDirection){
      let height = 15;
      let fiberIndex;
      const objIndex = indexes.objectIndex;
      const cableOrSplitter = cablesAndSplitters[objIndex];
      if(!cableOrSplitter){
        const id = this.getParentSplitter(cablesAndSplitters);
        const splitterObj = this.splittersCache.find(item => item.id === id);
        if(splitterObj){
          this.splittersCache.push({id: cablesAndSplitters.sp_id, height: splitterObj.height + 15});
          height = splitterObj.height + 15;
        }
      }
      if(cableOrSplitter && this.isCable(cableOrSplitter)){
        fiberIndex = indexes.entryIndex;
        const fiberMargins = this.getTubeFiberMargins(fiberIndex + 1, cableOrSplitter);
        const cableMargins = this.getCableMargins(objIndex + 1,cablesAndSplitters);
        height = (fiberMargins + cableMargins);
      }else if(cableOrSplitter && !this.isCable(cableOrSplitter)){
        height = this.getSplitterHeight(indexes, cableOrSplitter, direction, oppositeIndexes, oppositeCablesAndSplitters, oppositeDirection);
      }
      return height;
    },
    getParentSplitter(splitter){
      let spId;
      splitter.inPorts.forEach(port => {
        if(port.connectedPort){
          spId = port.connectedPort.pt_splitter_id;
        }
        return false;
      });
      return spId;
    },
    getSplitterHeight(indexes, splitter, direction , oppositeIndexes, oppositeCablesAndSplitters, oppositeDirection){
      let height;
      const splitterObj = this.splittersCache.find(item => item.id === splitter.sp_id);
      if(!splitterObj){
        height = this.getItemTopPosition(oppositeIndexes, oppositeCablesAndSplitters, oppositeDirection, indexes, splitter, direction);
        const {nivel, spHierarchy, parentSplitter} = this.getHierarchyOnSameSplitter(splitter);
        if(spHierarchy > 1){
          switch (spHierarchy) {
            case 3:
              const parentFound = this.splittersCache.find(item => item.id === parentSplitter.sp_id);
              if(parentFound){
                height = parentFound.height + (12.5 * 2) + (nivel * 35);
                this.splittersCache.push({id: splitter.sp_id, height});
              }
              break;
            case 2:
              const parent = this.splittersCache.find(item => item.id === parentSplitter.sp_id);
              if(parent){
                height = parent.height + (12.5) + (nivel * 35);
                this.splittersCache.push({id: splitter.sp_id, height});
              }else{
                const highestHeight = this.findHighestHeight([...this.splittersCache]);
                if(highestHeight){
                  height = height + highestHeight.height + 40
                }else{
                  height = height + 80;
                }
              }
            default:
              break;
          }
        }else{
          this.splittersCache.push({id: splitter.sp_id, height});
        }
      }else{
        height = splitterObj.height;
      }
      return height;
    },
    findHighestHeight(objects){
      let highestHeight = 0;
      let objectWithHighestHeight = null;

      for (let i = 0; i < objects.length; i++) {
        const { height } = objects[i];

        if (height > highestHeight) {
          highestHeight = height;
          objectWithHighestHeight = objects[i];
        }
      }

      return objectWithHighestHeight;
    },
    getHierarchyOnSameSplitter(splitter) {
      const spHierarchy = this.getSplitterHierarchy(splitter) - 1;
      let spIndex = 0;
      const parentId = this.getParentSplitter(splitter);
      let parentSplitter;
      if (parentId) {
        parentSplitter = this.splitters.find(item => item.sp_id === parentId);
        const parentFound = this.splittersCache.find(item => item.id === parentId);
        if(parentFound){
          parentSplitter.outPorts.forEach((port, index) =>{
            if(port.connectedPort && (port.connectedPort.pt_splitter_id === splitter.sp_id)){
              spIndex = index;
            }
          })
        }
      } 
      return {nivel: spIndex, spHierarchy, parentSplitter};
    },
    getTubeFiberMargins(fiberIndex, cable){
      let margin;
      const fibersPerTube = this.getFibersPerTube(cable);
      const tubeNumber = this.getTubeNumber(fibersPerTube, fiberIndex);
      const tubeMargin = this.getTubeMargin(tubeNumber);
      const fibersMargin = this.getFibersMargin(fiberIndex);
      const fibersInTube = this.getTubeNumberOfFiber(fibersPerTube, tubeNumber);
      if(fibersInTube === 1){
        //avoid adding 10px when tube has only 1 fiber
        margin = fibersMargin;
      }else{
        margin = (tubeMargin + fibersMargin)
      }
      return margin;
    },
    getTubeNumberOfFiber(fibersPerTube, tubeNumber){
      let numberOfFibers;
      const currentTubeFibers = fibersPerTube[tubeNumber];
      if(tubeNumber === '1'){
        numberOfFibers = currentTubeFibers
      }else{
        const previousNumberOfFibers = fibersPerTube[String(Number(tubeNumber) - 1)];
        numberOfFibers = (currentTubeFibers - previousNumberOfFibers)
      }
      return numberOfFibers;
    },
    getCableMargins(cableNumber, cablesAndSplitters){
      let margin;
      let previusCablesHeight = 0;
      if(cableNumber > 1){
        //check previous cables
        for(let i = cableNumber-2; i >=0 ; i--){
          if(this.isCable(cablesAndSplitters[i])){
            const id = String(cablesAndSplitters[i].cb_id);
            const cableDiv = document.getElementById(id);
            const cableHeight = cableDiv.getBoundingClientRect().height;
            previusCablesHeight += cableHeight;
          }
        }
        // add previous cables margins and previous cables height;
        margin = (5 * (cableNumber - 1)) + previusCablesHeight;
      }else{
        margin = 0
      }
      return margin
    },
    getFibersMargin(fiberIndex){
      let margin;
      if(fiberIndex > 1){
        // add previous fiber margins and previous fibers height;
        margin = (5 * (fiberIndex - 1)) + (25 * (fiberIndex - 1));
      }else{
        margin = 0;
      }
      return margin;
    },
    getTubeMargin(tubeNumber){
      let margin;
      if(tubeNumber > 1){
        margin = (10 * (tubeNumber-1));
      }else{
        margin = 0;
      }
      return margin
    },
    //get fibers in previous tubes
    getFibersPerTube(cable){
      const fiberPerTubeCache = {};
      const tubes = [];
      cable.fibers.forEach(fiber => {
        if(!tubes.includes(fiber.fb_nr_tube)){
          tubes.push(fiber.fb_nr_tube);
        }
      });
      tubes.forEach(tube =>{
        let count = 0; 
        cable.fibers.forEach(fiber =>{
          if(tube === fiber.fb_nr_tube){
            count++;
          }
        });
        if(tube > 1){
          fiberPerTubeCache[tube]= count + fiberPerTubeCache[tube-1];
        }else{
          fiberPerTubeCache[tube]= count;
        }
      });
      return fiberPerTubeCache
    },
    //get number of tube
    getTubeNumber(fibersPerTube, fiberIndex){
      // convert object to key's array
      const keys = Object.keys(fibersPerTube);
      let tubeNumber;
      for (let key of keys) {
        if(fibersPerTube[key] >= fiberIndex){
          tubeNumber = key
          break;
        }
      }
      return tubeNumber;
    },
    getConnectionLineCoordinates(
      start,
      left,
      right,
      width,
      anchorUnit,
      sameSide,
      side,
      firstAnchorValue ,
      secondAnchorValue,
      waterfallNivel,
      waterfallEnd,
      sameSideSplitter,
      sameSideNivel
    ) {
      let lineStart = `${start},` + left;
      let firstAnchor = firstAnchorValue * anchorUnit + "," + left;
      let secondAnchor = secondAnchorValue * anchorUnit + "," + (right + left) / 2;
      let lineEnd = width + "," + right;
      let line;

      //set anchors when is fiber same side
      if (sameSide && !sameSideSplitter) {
        let number = 2;
        if (side === "origin") {
          lineEnd = "0," + right;
        } else {
          lineStart = width + "," + left;
          number = 6;
        }
        firstAnchor = (number * anchorUnit + 55) + "," + left;
        secondAnchor = number * anchorUnit + "," + (right + left) / 2;
      }

      //set anchors when is splitter same side
      if(sameSide && sameSideSplitter){
        switch (sameSideNivel) {
          case 4:
            firstAnchor = ((firstAnchorValue * anchorUnit) + 65) + "," + (left);
            secondAnchor = ((secondAnchorValue * anchorUnit) + 10) + "," + ((right + left) / 2);
            lineStart = `${start + 15},` + left;
            break;
          case 2:
            firstAnchor = ((firstAnchorValue * anchorUnit) - 50) + "," + (left);
            secondAnchor = ((secondAnchorValue * anchorUnit) - 80) + "," + ((right + left) / 2);
            break;
          default:
            firstAnchor = ((firstAnchorValue * anchorUnit)) + "," + (left);
            secondAnchor = ((secondAnchorValue * anchorUnit) - 10) + "," + ((right + left) / 2);
            break;
        }
        if (side === "origin") {
          lineEnd = "0," + right;
        } else {
          lineStart = width + "," + left;
        }
      }

      if(waterfallEnd){
        switch (sameSideNivel) {
          case 4:
            firstAnchor = ((firstAnchorValue * anchorUnit) + 70) + "," + left;
            secondAnchor = ((secondAnchorValue * anchorUnit) + 30) + "," + (right + left) / 2;
            lineStart = `${start - 30},` + left;
            break;
          case 3:
            firstAnchor = ((firstAnchorValue * anchorUnit) + 60) + "," + left;
            secondAnchor = ((secondAnchorValue * anchorUnit) + 30) + "," + (right + left) / 2;
            lineStart = `${start - 70},` + left;
            break;
          default:
            firstAnchor = ((firstAnchorValue * anchorUnit) + 50) + "," + left;
            secondAnchor = ((secondAnchorValue * anchorUnit) + 30) + "," + (right + left) / 2;
            break;
        }
        line =  "M" + lineStart + " Q" + firstAnchor + " " + secondAnchor + " T" + lineEnd;
      }else if(waterfallNivel){
        lineStart = `${start},` + (left);
        line = "M" + lineStart + "C" + `${start+35} ${left}, ${start+30} ${left+15}, ${start+50} ${right}`;
      }else{
        switch (sameSideNivel) {
          case 4:
            if(!sameSide){
              lineStart = `${start + 40},` + left;
            }
            break;
          default:
            break;
        }
        line =  "M" + lineStart + " Q" + firstAnchor + " " + secondAnchor + " T" + lineEnd;
      }
      return (
        line
      );
    },

    isCable(object) {
      return object && object.hasOwnProperty("cb_id");
    },

    isSplitter(object) {
      return object && object.hasOwnProperty("sp_uuid");
    },
    openConnectionPointMenu(e, connectionPoint) {
      this.$emit("open-menu", { 
        event: e,
        origin: {
          connectionPoints: [...connectionPoint.splitter.inPorts], 
        },
        destination : {
          connectionPoints: [...connectionPoint.splitter.outPorts], 
        } 
      });
      this.$emit("set-splitter", 
          connectionPoint.splitter
      );
    },
    selectSplitter(splitter){
      let splitterClone = _.cloneDeep(splitter);
      splitterClone.type = "splitter";
      if(!this.selectedOrigin){
        splitterClone.ports = [...splitter.outPorts];
        this.$emit('select-item', 'origin', splitterClone);
      }else if(this.selectedOrigin){
        this.$emit('clear-selected-origin');
      }
      /*
      if(this.selectedOrigin){
        splitterClone.ports = [...splitter.inPorts];
        this.$emit('select-item', 'destination', splitterClone);
      }else if(!this.selectedOrigin){
        splitterClone.ports = [...splitter.outPorts];
        this.$emit('select-item', 'origin', splitterClone);
      }
      */
    },
    isSplitterSelected(splitterId) {
      return (this.selectedOrigin && this.selectedOrigin.hasOwnProperty('sp_id') && this.selectedOrigin.sp_id === splitterId)
    }
  }
};
</script>

<style scoped>
.svg-container{
  width: 100%;
  height: 100%;
}
.connections-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.connection_line {
  stroke-width: 1.5;
  stroke: black;
  fill: none;
  pointer-events: all;
}

.connection_line.passing,
.connection_line.splice {
  stroke: green;
}

.connection_line.connector {
  stroke: darkorange;
}

.img-container{
  width: 28px;
  height: 25px;
  position: absolute;
}
.img-container img {
  width: 100%;
  height: 100%;
}
.text-ellipsis {
  width: 50px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  padding-left: 5px;
}
.splitter-opacity{
  opacity: 0.75;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Splicing/Connections.vue